import {
  add,
  daysInMonth,
  diffMonths,
  diffMonthsExact,
  endOf,
  formatTokens,
  isValidDate,
  parseDate,
  startOf,
  subtract,
} from 'helpers/date_utils';
import { ISO_DATE_FORMAT } from 'helpers/formatters/date_time_formats';

import { updateCommission, updateCommissionTransactionSide } from './commissions';
import { round } from 'lodash';

export function updateLeaseTerm(leaseStructure, leaseTerm, changedKey) {
  leaseTerm = { ...leaseTerm };

  let leaseAmountAffected;
  switch (changedKey) {
  case 'leaseRate':
  case 'operatingExpensesRate':
    leaseAmountAffected = true;
    break;

  case 'startDate':
    handleStartDateChange(leaseTerm);
    leaseAmountAffected = true;
    break;

  case 'endDate':
    handleEndDateChange(leaseTerm);
    leaseAmountAffected = true;
    break;

  case 'months':
    handleMonthsChange(leaseTerm);
    leaseAmountAffected = true;
    break;
  }

  if (leaseTerm.months && leaseTerm.months <= 0) {
    leaseTerm.months = null;
  }

  if (changedKey === 'commissionAmount' || changedKey === 'commissionPercent') {
    leaseTerm[changedKey] = leaseTerm[changedKey] ? leaseTerm[changedKey] : 0;
  }

  if (leaseStructure.isRentCalculated) {
    if (leaseAmountAffected) {
      updateLeaseTermLeaseAmount(leaseTerm, leaseStructure.leaseRateUnits, leaseStructure.spaceSize);
    } else if (changedKey === 'leaseAmount') {
      updateLeaseRate(leaseTerm, leaseStructure.leaseRateUnits, leaseStructure.spaceSize);
    }
  }

  const commissionChanged = (
    changedKey === 'commissionAmount' ||
    changedKey === 'commissionPercent' ||
    changedKey === 'commissionRate'
  );

  if (commissionChanged) {
    handleCommissionChange(leaseTerm, changedKey);
  } else if (leaseAmountAffected && leaseTerm.transactionSideDetails) {
    // Handle lease amount changes that affect transaction side commissions
    leaseTerm.transactionSideDetails.forEach((transactionSideDetails) => {
      if (transactionSideDetails.grossAmount == null) transactionSideDetails.grossAmount = 0;
      if (transactionSideDetails.grossPercent == null) transactionSideDetails.grossPercent = 0;
      if (transactionSideDetails.grossRate == null) transactionSideDetails.grossRate = 0;

      updateCommissionTransactionSide(
        leaseTerm,
        leaseStructure.spaceSize,
        leaseStructure.commissionMode,
        transactionSideDetails.transactionSide
      );
    });
  }

  updateCommission(leaseTerm, leaseStructure.spaceSize, leaseStructure.commissionMode);

  return leaseTerm;
}

function handleStartDateChange(leaseTerm) {
  if (leaseTerm.startDate) {
    if (leaseTerm.endDate) {
      leaseTerm.months = calculateMonths(leaseTerm.startDate, leaseTerm.endDate);
    } else if (leaseTerm.months) {
      leaseTerm.endDate = calculateEndDate(leaseTerm.startDate, leaseTerm.months);
    }
  } else {
    leaseTerm.months = null;
  }
}

function handleEndDateChange(leaseTerm) {
  if (leaseTerm.endDate) {
    if (leaseTerm.startDate) {
      leaseTerm.months = calculateMonths(leaseTerm.startDate, leaseTerm.endDate);
    }
  } else {
    leaseTerm.months = null;
  }
}

function handleMonthsChange(leaseTerm) {
  leaseTerm.endDate = leaseTerm.months == null ? null : calculateEndDate(leaseTerm.startDate, leaseTerm.months);
}

function handleCommissionChange(leaseTerm, key) {
  if (key === 'commissionAmount') {
    leaseTerm.commissionUnits = 'commission_units_dollars';
  } else {
    leaseTerm.commissionUnits = 'commission_units_percent';
  }
}

function handleTransactionSideChange(transactionSideDetails, key) {
  if (key === 'grossAmount') {
    transactionSideDetails.grossUnit = 'unit_dollars';
  } else {
    transactionSideDetails.grossUnit = 'unit_percent';
  }
}

function updateLeaseTermLeaseAmount(leaseTerm, leaseRateUnits, spaceSize) {
  const perSpaceSize = /per_space_size/.test(leaseRateUnits);
  const perYear = /per_year/.test(leaseRateUnits);

  const totalRate = leaseTerm.leaseRate + leaseTerm.operatingExpensesRate;
  const hasRate = totalRate != null && !isNaN(totalRate);
  const hasAllDates = !!leaseTerm.startDate && !!leaseTerm.endDate && !!leaseTerm.months;

  if (!hasRate || !hasAllDates || (perSpaceSize && spaceSize == null)) {
    leaseTerm.leaseAmount = null;
  } else {
    leaseTerm.leaseAmount = calculateLeaseAmountPerMonth(leaseTerm) / (perYear ? 12 : 1);

    if (perSpaceSize) {
      leaseTerm.leaseAmount *= spaceSize;
    }
  }
}

function calculateLeaseAmountPerMonth({ startDate, endDate, leaseRate, operatingExpensesRate }) {
  startDate = parseDate(startDate);
  endDate = parseDate(endDate);
  const totalRate = leaseRate + operatingExpensesRate;

  let calculatedAmount = 0;
  if (startDate.getDate() !== 1) {
    const proratedDays = daysInMonth(startDate) - startDate.getDate() + 1;
    calculatedAmount += proratedDays / daysInMonth(startDate) * totalRate;
    // we need use the next month us the first whole month after we prorate
    startDate = add(startDate, 1, 'month');
    startDate.setDate(1);
  }

  if (daysInMonth(endDate) !== endDate.getDate()) {
    const moveOutDate = endDate.getDate();
    calculatedAmount += moveOutDate / daysInMonth(endDate) * totalRate;
    // we need to use the previous month as the last whole month after we prorate
    endDate = startOf(endOf(subtract(endDate, 1, 'month'), 'month'), 'day');
  }

  // End date is exclusive (e.g. 20xx-01-01 to 20xx-01-31 should be one month rather than 20xx-02-01)
  const wholeMonths = diffMonths(add(endDate, 1, 'day'), startDate);
  calculatedAmount += wholeMonths * totalRate;

  return calculatedAmount;
}

function updateLeaseRate(leaseTerm, leaseRateUnits, spaceSize) {
  const perSpaceSize = /per_space_size/.test(leaseRateUnits);
  const perYear = /per_year/.test(leaseRateUnits);

  if (leaseTerm.leaseAmount == null || leaseTerm.months == null || (perSpaceSize && spaceSize == null)) {
    leaseTerm.leaseRate = null;
  } else {
    let totalRate = leaseTerm.leaseAmount / leaseTerm.months * (perYear ? 12 : 1);

    if (perSpaceSize) {
      totalRate /= spaceSize;
    }

    leaseTerm.leaseRate = totalRate - leaseTerm.operatingExpensesRate;
  }
}

function calculateMonths(startDate, endDate) {
  // End date is exclusive (e.g. 20xx-01-01 to 20xx-01-31 should be one month rather than 20xx-02-01)
  return diffMonthsExact(add(parseDate(endDate), 1, 'day'), parseDate(startDate));
}

function calculateEndDate(startDate, months) {
  const wholePart = Math.floor(months);
  const fractionalPart = months - wholePart;
  let result = add(parseDate(startDate), wholePart, 'months');

  if (fractionalPart > 0) {
    const fractionalDays = Math.round(fractionalPart * daysInMonth(result));
    result = add(result, fractionalDays, 'days');
  }

  const endDate = subtract(result, 1, 'day');
  return isValidDate(endDate) ? formatTokens(endDate, ISO_DATE_FORMAT) : null;
}

export function calculateLeaseTermsFromEscalation(escalation, leaseStructure) {
  const {
    buySideCommissionPercent,
    buySideCommissionRate,
    commissionPercent,
    commissionRate,
    durationUnits,
    escalationFrequencyUnits,
    escalationRate,
    escalationRateUnits,
    freeRent,
    initialLeaseRate,
    operatingExpensesRate,
    sellSideCommissionPercent,
    sellSideCommissionRate,
    startDate,
    useFreeRent,
    useTransactionSideValues,
  } = escalation;

  const leaseTerms = [];
  let { duration, escalationFrequency } = escalation, index = leaseStructure.leaseTerms.length;
  let previousLeaseRate = initialLeaseRate, previousStartDate = parseDate(startDate);

  duration = durationUnits === 'years' ? duration * 12 : duration;
  escalationFrequency = escalationFrequencyUnits === 'years' ? escalationFrequency * 12 : escalationFrequency;

  // A missing or non-positive escalation frequency means "no escalation": generate a
  // single flat term covering the full lease duration.
  if (!(escalationFrequency > 0)) {
    escalationFrequency = duration;
  }

  const initialDuration = duration;

  for (duration; duration > 0; duration -= escalationFrequency) {
    const months = Math.min(duration, escalationFrequency);
    let newStartDate = previousStartDate, newLeaseRate = previousLeaseRate;

    if (duration !== initialDuration) {
      newStartDate = add(newStartDate, escalationFrequency, 'months');

      if (escalationRateUnits === 'percent') {
        newLeaseRate = newLeaseRate * (1 + escalationRate / 100);
      } else {
        newLeaseRate += escalationRate;
      }

      newLeaseRate = round(newLeaseRate, 2);
    }

    previousStartDate = newStartDate;
    previousLeaseRate = newLeaseRate;

    const transactionSideDetails = [];
    let calculatedCommissionPercent = commissionPercent || 0;
    let calculatedCommissionRate = commissionRate || 0;

    if (useTransactionSideValues) {
      transactionSideDetails.push(
        {
          ...buildNewTransactionSideDetails('buy_side'),
          grossPercent: buySideCommissionPercent || 0,
          grossRate: buySideCommissionRate || 0,
        },
        {
          ...buildNewTransactionSideDetails('sell_side'),
          grossPercent: sellSideCommissionPercent || 0,
          grossRate: sellSideCommissionRate || 0,
        }
      );
      calculatedCommissionPercent = transactionSideDetails.reduce((sum, details) => sum + details.grossPercent, 0);
      calculatedCommissionRate = transactionSideDetails.reduce((sum, details) => sum + details.grossRate, 0);
    }

    const leaseTerm = {
      commissionUnits: 'commission_units_percent',
      commissionPercent: calculatedCommissionPercent,
      commissionRate: calculatedCommissionRate,
      endDate: calculateEndDate(newStartDate, months),
      index: index++,
      leaseRate: newLeaseRate,
      months: months,
      operatingExpensesRate,
      startDate: formatTokens(newStartDate, ISO_DATE_FORMAT),
      transactionSideDetails,
    };
    updateLeaseTermLeaseAmount(leaseTerm, leaseStructure.leaseRateUnits, leaseStructure.spaceSize);

    leaseTerms.push(leaseTerm);
  }

  if (useFreeRent && freeRent > 0 && leaseTerms.length > 0) {
    applyFreeRent(leaseTerms, escalation, leaseStructure);
  }

  return leaseTerms;
}

function applyFreeRent(leaseTerms, escalation, leaseStructure) {
  const { freeRent, freeRentUnits, operatingExpensesRate, useTransactionSideValues } = escalation;
  const freeRentMonths = freeRentUnits === 'years' ? freeRent * 12 : freeRent;
  const leaseStartDate = leaseTerms[0].startDate;
  let remaining = freeRentMonths;

  while (remaining > 0 && leaseTerms.length > 0) {
    const firstTerm = leaseTerms[0];
    if (firstTerm.months <= remaining) {
      remaining -= firstTerm.months;
      leaseTerms.shift();
    } else {
      firstTerm.startDate = formatTokens(add(parseDate(firstTerm.startDate), remaining, 'months'), ISO_DATE_FORMAT);
      firstTerm.months -= remaining;
      updateLeaseTermLeaseAmount(firstTerm, leaseStructure.leaseRateUnits, leaseStructure.spaceSize);
      remaining = 0;
    }
  }

  const freeRentTerm = {
    commissionUnits: 'commission_units_percent',
    commissionPercent: 0,
    commissionRate: 0,
    endDate: calculateEndDate(leaseStartDate, freeRentMonths),
    leaseRate: 0,
    months: freeRentMonths,
    operatingExpensesRate,
    startDate: leaseStartDate,
    transactionSideDetails: useTransactionSideValues
      ? [buildNewTransactionSideDetails('buy_side'), buildNewTransactionSideDetails('sell_side')]
      : [],
  };
  updateLeaseTermLeaseAmount(freeRentTerm, leaseStructure.leaseRateUnits, leaseStructure.spaceSize);

  leaseTerms.unshift(freeRentTerm);
  leaseTerms.forEach((term, i) => term.index = leaseStructure.leaseTerms.length + i);
}

export function getCommissionRate(leaseTerm, transactionSide) {
  if (!transactionSide) return leaseTerm.commissionRate;

  return leaseTerm.transactionSideDetails.find(details => details.transactionSide === transactionSide).grossRate;
}

export function getCommissionAmount(leaseTerm, transactionSide) {
  if (!transactionSide) return leaseTerm.commissionAmount;

  return leaseTerm.transactionSideDetails.find(details => details.transactionSide === transactionSide).grossAmount;
}

export function getCommissionPercent(leaseTerm, transactionSide) {
  if (!transactionSide) return leaseTerm.commissionPercent;

  return leaseTerm.transactionSideDetails.find(details => details.transactionSide === transactionSide).grossPercent;
}

export function updateLeaseTermByTransactionSide(
  leaseStructure,
  leaseTerm,
  key = null,
  value = null,
  transactionSide = null
) {
  for (const transactionSideDetails of leaseTerm.transactionSideDetails) {
    if (transactionSide && transactionSideDetails.transactionSide !== transactionSide) {
      continue;
    }

    if (key === 'grossAmount' || key === 'grossPercent' || key === 'grossRate') {
      transactionSideDetails[key] = value;
      handleTransactionSideChange(transactionSideDetails, key);
    }

    updateCommissionTransactionSide(
      leaseTerm,
      leaseStructure.spaceSize,
      leaseStructure.commissionMode,
      transactionSideDetails.transactionSide
    );
  }

  const leaseTermAmount = leaseTerm.transactionSideDetails.reduce((sum, details) => sum + details.grossAmount, 0);

  return updateLeaseTerm(leaseStructure, { ...leaseTerm, commissionAmount: leaseTermAmount }, 'commissionAmount');
}

function buildNewTransactionSideDetails(transactionSide) {
  return {
    transactionSide,
    grossAmount: 0,
    grossPercent: 0,
    grossRate: 0,
    grossUnit: 'unit_percent'
  };
}
